/**
 * Copy for the "Mark this task complete?" confirmations on the task and
 * sub-task checkboxes in TaskDetailRow.
 *
 * Checking a task is not a single-row write: completing a parent cascades to
 * every open sub-task (TaskDetailRow.toggleStatus), and completing the last open
 * sub-task completes its parent (toggleSubTask). Neither cascade is reversible
 * from the same checkbox — un-completing a task does NOT reopen the sub-tasks it
 * closed — so the confirmation names what is about to happen rather than just
 * asking "are you sure".
 *
 * The sentences returned here render after "<title> will be marked done."; an
 * empty array is normal and means the click does exactly that and nothing else.
 */

export interface TaskCompleteWarningInput {
  openSubTaskCount: number;
  isClientTask?: boolean;
  /** Client tasks only reach the portal once they've been sent. */
  sentAt?: string | null;
}

export interface SubTaskCompleteWarningInput {
  isLastOpenSubTask: boolean;
  parentTitle?: string | null;
  parentDone?: boolean;
}

export function buildTaskCompleteWarning(input: TaskCompleteWarningInput): string[] {
  const lines: string[] = [];

  const openSubs = Math.max(0, input.openSubTaskCount ?? 0);
  if (openSubs > 0) {
    const plural = openSubs === 1 ? "sub-task" : "sub-tasks";
    lines.push(
      `Its ${openSubs} open ${plural} will also be marked done — unchecking this task later won't reopen them.`,
    );
  }

  // Only a SENT client task is visible in the portal (v1.2.80 gates the portal
  // task list on sent_at), so an unsent one gets no portal claim.
  if (input.isClientTask && input.sentAt) {
    lines.push("This is a client task — the client sees it as done in their portal.");
  }

  return lines;
}

export function buildSubTaskCompleteWarning(input: SubTaskCompleteWarningInput): string[] {
  // toggleSubTask only auto-completes a parent that is still open.
  if (!input.isLastOpenSubTask || input.parentDone) return [];
  const parent = input.parentTitle ? `"${input.parentTitle}"` : "the parent task";
  return [`This is the last open sub-task, so ${parent} will be marked done too.`];
}
